import VisibilityTrackingPlugin from '@spt-tracking/engage-visibility-tracking-plugin';
import { buildViewArticleEvent } from './view/article/event-builder.js';
import { buildViewSalesPosterEvent } from './view/salesposter/event-builder.js';
import { buildViewSalesPosterEvent as buildViewSalesPosterForNorwegianBrandsEvent } from './view/salesposter/for-norwegian-brands/event-builder.js';
import { buildViewListingEvent } from './view/listing/event-builder.js';
import { buildViewFrontpageEvent } from './view/frontpage/event-builder.js';
import { buildViewPageEvent } from './view/page/event-builder.js';
import { buildViewLoginPosterEvent } from './view/loginposter/event-builder.js';
import { buildViewUIElementEvent } from './view/ui-element/event-builder.js';
import { buildViewUIElementWithPluginEvent } from './view/ui-element/engage-visibility-tracking-plugin/event-builder.js';
import { buildClickUIElementEvent } from './click/ui-element/event-builder.js';
class NewsMediaPulseTracker {
    constructor(pulseTracker, actorPromise) {
        this.trackViewArticle = (inputProperties, trackOptions) => {
            const pageViewEventData = buildViewArticleEvent(inputProperties);
            return this.pulseTracker.trackPageView(pageViewEventData, trackOptions);
        };
        this.trackViewLoginPoster = (inputProperties, trackOptions) => {
            const viewPageEventData = buildViewLoginPosterEvent(inputProperties);
            return this.pulseTracker.trackPageView(viewPageEventData, trackOptions);
        };
        this.trackViewSalesPoster = (inputProperties, trackOptions) => {
            const viewSalesposterEventData = buildViewSalesPosterEvent(inputProperties);
            return this.pulseTracker.trackPageView(viewSalesposterEventData, trackOptions);
        };
        this.trackViewSalesPosterForNorwegianBrands = (inputProperties, trackOptions) => {
            const viewSalesposterEventData = buildViewSalesPosterForNorwegianBrandsEvent(inputProperties);
            return this.pulseTracker.trackPageView(viewSalesposterEventData, trackOptions);
        };
        this.trackViewListing = (inputProperties, trackOptions) => {
            const viewListingEventData = buildViewListingEvent(inputProperties);
            return this.pulseTracker.trackPageView(viewListingEventData, trackOptions);
        };
        this.trackViewFrontpage = (inputProperties, trackOptions) => {
            const viewFrontpageEventData = buildViewFrontpageEvent(inputProperties);
            return this.pulseTracker.trackPageView(viewFrontpageEventData, trackOptions);
        };
        this.trackViewPage = (inputProperties, trackOptions) => {
            const viewPageEventData = buildViewPageEvent(inputProperties);
            return this.pulseTracker.trackPageView(viewPageEventData, trackOptions);
        };
        /**
         * {@link @spt-tracking/pulse-sdk/dist/types/_tracker.d.ts}
         * Start sending events when users leave a page.
         * This will register handlers on the browser events 'pagehide', 'popstate' and 'visibilitychange'
         * and will send a Leave event when these are triggered. The leave event will be a copy of the last sent view-event
         * + engagement values. The fields in the default
         * event can be found here: https://pages.github.schibsted.io/mpt-data/pulse-standard/#leave-article.
         * The Leave event can be expanded further by passing an event-builder as the third param, but this is not recommended.
         *
         * Example usage:
         *
         * ```ts
         *  const pageElement = document.querySelector('#application') || document.body;
         *  const articleElement = document.getElementById('article-element');
         *  tracker.addPageLeaveTracking(articleElement, pageElement);
         * ```
         *
         * Passing '\{ objectResizable: true \}' as the 4th param makes the SDK re-calculate the object height whenever it changes,
         * which is useful for when the page or object changes size after loading, by ex
         * ```
         *  ...
         *  tracker.addPageLeaveTracking(articleElement, pageElement, undefined, {
         *    objectResizable: true,
         *  });
         * ```
         *
         * Keep in mind that the Beacon API is used to send events to page leave, and in order to guarantee as good as
         * possible that these events are sent, the event creator parameter should be synchronous.
         *
         * For the same reasons, we advise that the event creator parameter returns an event that does not contain any promises.
         *
         * @param {Element} objectElement The DOM element that is used to calculate various view-related fields in the default event.
         * @param {Element} pageElement The DOM element that represents the page that the user is currently on.
         * @param {LeaveEventBuilder} eventBuilder Function that returns an event to send when user leaves the page.
         * @param {LeaveEventOptions} options
         * @returns {(leaveEvent: LeaveEvent) => void} A function to update the event to send on page leave.
         * @category Leave Event
         */
        this.addPageLeaveTracking = (objectElement, pageElement, eventBuilder, options) => {
            return this.pulseTracker.addPageLeaveTracking(objectElement, pageElement, eventBuilder, options);
        };
        /**
         * {@link @spt-tracking/pulse-sdk/dist/types/_tracker.d.ts}
         * Stop sending events when users leave a page.
         * Removes all event handlers, meaning that to event will be sent if the user leaves the page.
         * @category Leave Event
         */
        this.removePageLeaveTracking = () => {
            return this.pulseTracker.removePageLeaveTracking();
        };
        /**
         * {@link @spt-tracking/pulse-sdk/dist/types/_tracker.d.ts}
         * Update leave tracking.
         * Updates the DOM element used to calculate the default fields in the Leave event.
         * @param {Element} objectElement The DOM element that is used to calculate various view-related fields in the default event.
         * @param {Element} pageElement The DOM element that represents the page that the user is currently on.
         * @param {boolean} resetProperties A flag that decided whether the properties of the default Leave event should
         * be reset when the tracking is updated. Defaults to false.
         * @category Leave Event
         */
        this.updatePageLeaveTracking = (objectElement, pageElement, resetProperties) => {
            return this.pulseTracker.updatePageLeaveTracking(objectElement, pageElement, resetProperties);
        };
        /**
         * {@link @spt-tracking/pulse-sdk/dist/types/_tracker.d.ts}
         * Manually trigger a Leave event
         * Useful if for example the consuming website is an SPA and the normal browser events are not triggered.
         * is generated internally in the SDK.
         * @param {LeaveEvent=} eventInput
         * @param {eventName=} eventName The optional name of the browser event to simulate.
         * @category Leave Event
         */
        this.trackPageLeave = (eventInput, eventName) => {
            return this.pulseTracker.trackPageLeave(eventInput, eventName);
        };
        /**
         * {@link @spt-tracking/pulse-sdk/dist/types/_tracker.d.ts}
         * Manually trigger a Leave event only if Page Leave tracking is active in the SDK
         * This method works similarly to
         * ```
         * trackPageLeave(event: LeaveEvent, eventName?: string)
         * ```
         * with the added guarantee that a second Leave event will not be triggered if one already has been sent.
         * is generated internally in the SDK.
         * @param {LeaveEvent=} eventInput
         * @param {eventName=} eventName The optional name of the browser event to simulate.
         * @category Leave Event
         */
        this.trackActivePageLeave = (eventInput, eventName) => {
            return this.pulseTracker.trackActivePageLeave(eventInput, eventName);
        };
        this.trackViewUIElement = (inputProperties, trackOptions) => {
            const viewUIElementEventData = buildViewUIElementEvent(inputProperties);
            return this.pulseTracker.track('trackerEvent', viewUIElementEventData, trackOptions);
        };
        this.registerViewUIElementTracking = (pluginConfig) => {
            const updatedBaseEvent = buildViewUIElementWithPluginEvent(pluginConfig.event);
            const visibilityTracker = new VisibilityTrackingPlugin(this.pulseTracker, {
                ...pluginConfig,
                event: updatedBaseEvent,
            });
            visibilityTracker.start();
        };
        this.trackClickUIElement = (inputProperties, trackOptions) => {
            const clickUIElementEventData = buildClickUIElementEvent(inputProperties);
            return this.pulseTracker.track('trackerEvent', clickUIElementEventData, trackOptions);
        };
        this.pulseTracker = pulseTracker;
        if (actorPromise) {
            this.pulseTracker.update({ actor: actorPromise });
        }
    }
}
NewsMediaPulseTracker.fromAutotracker = (pulseAutotracker, actorPromise) => {
    return new Promise((resolve, reject) => {
        pulseAutotracker((pulseTracker) => {
            if (pulseTracker) {
                resolve(new NewsMediaPulseTracker(pulseTracker, actorPromise));
            }
            else {
                reject(new Error('Error getting pulse tracker'));
            }
        });
    });
};
export { NewsMediaPulseTracker };
//# sourceMappingURL=NewsMediaPulseTracker.js.map